import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Building2, Wrench, Zap, Briefcase, Droplets, Scale, GraduationCap, Gavel, Lightbulb, ArrowRight, CheckCircle2, MessageSquare, AlertTriangle, Factory } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { listServices } from '../services/api';

const ServicesPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [expandedCard, setExpandedCard] = useState(null);
  const [apiServices, setApiServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping for service categories
  const iconMap = {
    'MPCB': Factory,
    'DISH': Shield,
    'DIC': Building2,
    'CGWA': Droplets,
    'MIDC': Building2,
    'MSEB': Zap,
    'Labor': Briefcase,
    'FairPrice': Scale,
    'HazardousWaste': AlertTriangle
  };

  // Gradient mapping for service categories
  const gradientMap = {
    'MPCB': 'from-green-600 via-emerald-600 to-teal-600',
    'DISH': 'from-blue-600 via-blue-700 to-blue-800',
    'DIC': 'from-slate-600 via-slate-700 to-slate-800',
    'CGWA': 'from-cyan-400 via-blue-500 to-teal-500',
    'MIDC': 'from-slate-600 via-slate-700 to-slate-800',
    'MSEB': 'from-slate-600 via-slate-700 to-slate-800',
    'Labor': 'from-primary-600 via-primary-700 to-blue-700',
    'FairPrice': 'from-slate-600 via-slate-700 to-slate-800',
    'HazardousWaste': 'from-orange-600 via-red-600 to-red-700'
  };

  // Background color mapping
  const bgColorMap = {
    'MPCB': 'bg-green-50',
    'DISH': 'bg-blue-50',
    'DIC': 'bg-slate-50',
    'CGWA': 'bg-cyan-50',
    'MIDC': 'bg-slate-50',
    'MSEB': 'bg-slate-50',
    'Labor': 'bg-primary-50',
    'FairPrice': 'bg-slate-50',
    'HazardousWaste': 'bg-orange-50'
  };

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const services = await listServices();
        setApiServices(services || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        setApiServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);


  // Convert API service to display format with language support
  const convertApiService = (apiService) => {
    const category = apiService.category || '';
    const Icon = iconMap[category] || Shield;
    // Use color from database, fallback to category mapping
    const gradient = apiService.color || gradientMap[category] || 'from-primary-600 via-primary-700 to-primary-800';
    const bgColor = apiService.bgColor || bgColorMap[category] || 'bg-primary-50';
    
    // Get multilingual data - handle both object and string formats
    const getName = (nameObj) => {
      if (typeof nameObj === 'string') return nameObj;
      if (typeof nameObj === 'object' && nameObj !== null) {
        return nameObj.en || nameObj.mr || nameObj.hi || 'Service';
      }
      return 'Service';
    };
    
    const getDescription = (descObj) => {
      if (typeof descObj === 'string') return descObj;
      if (typeof descObj === 'object' && descObj !== null) {
        return descObj.en || descObj.mr || descObj.hi || '';
      }
      return '';
    };
    
    const getBenefits = (benefitsObj) => {
      if (Array.isArray(benefitsObj)) return benefitsObj;
      if (typeof benefitsObj === 'object' && benefitsObj !== null) {
        return benefitsObj.en || benefitsObj.mr || benefitsObj.hi || [];
      }
      return [];
    };
    
    const name = apiService.name || {};
    const description = apiService.description || {};
    const detailedDescription = apiService.detailedDescription || {};
    const benefits = apiService.benefits || {};
    
    // Extract string values safely
    const nameEn = typeof name === 'string' ? name : (name.en || 'Service');
    const nameMr = typeof name === 'string' ? name : (name.mr || name.en || 'Service');
    const nameHi = typeof name === 'string' ? name : (name.hi || name.en || 'Service');
    
    const descEn = typeof description === 'string' ? description : (description.en || '');
    const descMr = typeof description === 'string' ? description : (description.mr || description.en || '');
    const descHi = typeof description === 'string' ? description : (description.hi || description.en || '');
    
    const detailedDescEn = typeof detailedDescription === 'string' ? detailedDescription : (detailedDescription.en || '');
    const detailedDescMr = typeof detailedDescription === 'string' ? detailedDescription : (detailedDescription.mr || detailedDescription.en || '');
    const detailedDescHi = typeof detailedDescription === 'string' ? detailedDescription : (detailedDescription.hi || detailedDescription.en || '');
    
    const benefitsEn = Array.isArray(benefits) ? benefits : (benefits.en || []);
    const benefitsMr = Array.isArray(benefits) ? benefits : (benefits.mr || benefits.en || []);
    const benefitsHi = Array.isArray(benefits) ? benefits : (benefits.hi || benefits.en || []);
    
    return {
      id: apiService._id || apiService.id,
      name: nameEn,
      nameMr: nameMr,
      nameHi: nameHi,
      icon: Icon,
      gradient: gradient,
      bgColor: bgColor,
      isFromAPI: true,
      english: {
        title: nameEn,
        description: descEn || detailedDescEn,
        features: benefitsEn
      },
      marathi: {
        title: nameMr,
        description: descMr || detailedDescMr || descEn,
        features: benefitsMr
      },
      hindi: {
        title: nameHi,
        description: descHi || detailedDescHi || descEn,
        features: benefitsHi
      }
    };
  };

  // Use only API services (no hardcoded data)
  const allServices = apiServices.map(convertApiService);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const renderServiceCard = (service) => {
    const Icon = service.icon;
    const isEnglish = currentLang === 'en';
    const isHindi = currentLang === 'hi';
    const isMarathi = currentLang === 'mr';
    
    // Get content based on language
    let content;
    if (isEnglish) {
      content = service.english;
    } else if (isHindi) {
      content = service.hindi || service.marathi || service.english;
    } else {
      content = service.marathi || service.english;
    }
    
    // Get service name based on language
    let heading = service.name;
    if (isMarathi && service.nameMr) {
      heading = service.nameMr;
    } else if (isHindi && service.nameHi) {
      heading = service.nameHi;
    } else if (isEnglish) {
      heading = service.name;
    }
    
    const isExpanded = expandedCard === service.id;
    
    return (
      <div 
        key={service.id} 
        className="group cursor-pointer"
        onClick={() => toggleCard(service.id)}
      >
        <div className={`relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col ${isExpanded ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}`}>
          {/* Gradient Header */}
          <div className={`bg-gradient-to-br ${service.gradient} p-5 md:p-6 lg:p-7 text-white relative overflow-hidden`}>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <Icon className="h-7 w-7 md:h-8 md:w-8" />
                <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                  <ArrowRight className="h-5 w-5 md:h-6 md:w-6" />
                </div>
              </div>
              <h3 className={`text-base md:text-lg lg:text-xl font-bold mb-2 md:mb-3 leading-tight ${isHindi || isMarathi ? 'font-hindi-marathi' : ''}`} lang={isHindi ? 'hi' : isMarathi ? 'mr' : 'en'}>
                {heading}
              </h3>
              <h4 className={`text-xs md:text-sm font-semibold opacity-95 ${isHindi || isMarathi ? 'font-hindi-marathi' : ''}`} lang={isHindi ? 'hi' : isMarathi ? 'mr' : 'en'}>
                {content.title}
              </h4>
            </div>
          </div>

          {/* Content Body */}
          <div className={`${service.bgColor} p-5 md:p-6 lg:p-7 flex-grow transition-all duration-500`}>
            <p className={`text-sm md:text-base text-gray-700 mb-4 md:mb-5 leading-relaxed ${isHindi || isMarathi ? 'font-hindi-marathi' : ''}`} lang={isHindi ? 'hi' : isMarathi ? 'mr' : 'en'}>
              {content.description}
            </p>
            
            {/* Features - Expandable */}
            {content.features && content.features.length > 0 && (
              <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pt-4 md:pt-5 border-t border-gray-200 space-y-2.5 md:space-y-3">
                  {content.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-2.5 md:gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-primary-600" />
                      </div>
                      <p className={`text-xs md:text-sm text-gray-700 leading-relaxed flex-1 ${isHindi || isMarathi ? 'font-hindi-marathi' : ''}`} lang={isHindi ? 'hi' : isMarathi ? 'mr' : 'en'}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* View Details Button */}
            {!isExpanded && (
              <div className="mt-5 md:mt-6 pt-4 md:pt-5 border-t border-gray-200">
                <div className="flex items-center text-primary-600 font-semibold text-sm md:text-base group-hover:gap-2 transition-all">
                  <span>{getText('services.viewDetails')}</span>
                  <ArrowRight className="h-4 w-4 md:h-5 md:w-5 ml-1.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-grey to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-10 md:py-14 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5 md:mb-6 leading-tight">
              {getText('services.title')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('services.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600">{getText('services.loading')}</p>
            </div>
          ) : allServices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {allServices.map(renderServiceCard)}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">{getText('services.noServices')}</p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default ServicesPage;
